// Shared utilities for people search — used by both standalone container and modal

import {
  decomposeLocationValues,
  splitLocationParts,
} from './location-components.util';

// ─── Job Title Abbreviation Expansion ────────────────────────────────

export const JOB_TITLE_ABBREVIATION_EXPANSIONS: Record<string, string[]> = {
  ceo: ['chief executive officer'],
  cfo: ['chief financial officer'],
  coo: ['chief operating officer'],
  cto: ['chief technology officer'],
  cio: ['chief information officer'],
  cmo: ['chief marketing officer'],
  cro: ['chief revenue officer'],
  chro: ['chief human resources officer'],
  cpo: ['chief product officer'],
  cso: ['chief strategy officer'],
  ciso: ['chief information security officer'],
  vp: ['vice president'],
  svp: ['senior vice president'],
  evp: ['executive vice president'],
  avp: ['assistant vice president'],
  gm: ['general manager'],
  swe: ['software engineer'],
  sde: ['software development engineer'],
  se: ['software engineer'],
  sse: ['senior software engineer'],
  fs: ['full stack engineer'],
  fe: ['frontend engineer'],
  be: ['backend engineer'],
  ml: ['machine learning engineer'],
  mle: ['machine learning engineer'],
  ai: ['artificial intelligence engineer'],
  ds: ['data scientist'],
  de: ['data engineer'],
  qa: ['quality assurance engineer'],
  sdet: ['software development engineer in test'],
  sre: ['site reliability engineer'],
  pm: ['product manager'],
  tpm: ['technical program manager'],
  em: ['engineering manager'],
  ae: ['account executive'],
  am: ['account manager'],
  sdr: ['sales development representative'],
  bdr: ['business development representative'],
  csm: ['customer success manager'],
  mop: ['marketing operations manager'],
  revops: ['revenue operations'],
  salesops: ['sales operations'],
  hr: ['human resources'],
  hrbp: ['human resources business partner'],
  ta: ['talent acquisition'],
  rec: ['recruiter'],
  ops: ['operations manager'],
  bizops: ['business operations'],
  finops: ['finance operations'],
  fpa: ['financial planning and analysis'],
  gc: ['general counsel'],
  bdm: ['business development manager'],
};

// Token-level abbreviation map for fragments that appear WITHIN compound titles.
// Different from the full-title map: "ml" -> "machine learning" (not "machine learning engineer").
const TOKEN_ABBREVIATIONS: Record<string, string> = {
  // C-suite (these are also full titles, but can appear in compounds like "CEO & Co-Founder")
  ceo: 'chief executive officer',
  cfo: 'chief financial officer',
  coo: 'chief operating officer',
  cto: 'chief technology officer',
  cio: 'chief information officer',
  cmo: 'chief marketing officer',
  cro: 'chief revenue officer',
  chro: 'chief human resources officer',
  cpo: 'chief product officer',
  cso: 'chief strategy officer',
  ciso: 'chief information security officer',
  // Leadership levels
  vp: 'vice president',
  svp: 'senior vice president',
  evp: 'executive vice president',
  avp: 'assistant vice president',
  gm: 'general manager',
  // Functional areas (no role suffix - these are MODIFIERS, not complete titles)
  ml: 'machine learning',
  ai: 'artificial intelligence',
  hr: 'human resources',
  qa: 'quality assurance',
  // Ops variants
  revops: 'revenue operations',
  salesops: 'sales operations',
  bizops: 'business operations',
  finops: 'finance operations',
  ops: 'operations',
  // Roles that commonly appear as fragments
  pm: 'product manager',
  tpm: 'technical program manager',
  em: 'engineering manager',
  swe: 'software engineer',
  sde: 'software development engineer',
  sre: 'site reliability engineer',
  sdr: 'sales development representative',
  bdr: 'business development representative',
  bdm: 'business development manager',
};

// Stop words: connectors/prepositions in titles. Never expanded, never count toward cap.
const STOP_WORDS = new Set([
  'of',
  'and',
  'the',
  'in',
  'for',
  'at',
  'to',
  'a',
  'an',
  '&',
  '-',
  '/',
  'or',
  'with',
]);

// Build reverse index: "chief executive officer" -> ["ceo"]
const REVERSE_TITLE_MAP: Record<string, string[]> = {};
for (const [abbr, expansions] of Object.entries(
  JOB_TITLE_ABBREVIATION_EXPANSIONS
)) {
  for (const expansion of expansions) {
    const key = expansion.toLowerCase();
    if (!REVERSE_TITLE_MAP[key]) REVERSE_TITLE_MAP[key] = [];
    REVERSE_TITLE_MAP[key].push(abbr);
  }
}

// Build sorted multi-word reverse spans for greedy matching.
// Sorted by word count DESC so "senior vice president" matches before "vice president".
const MULTI_WORD_REVERSE_SPANS: Array<{ words: string[]; abbr: string }> = [];
for (const [abbr, expansion] of Object.entries(TOKEN_ABBREVIATIONS)) {
  const words = expansion.split(' ');
  if (words.length > 1) {
    MULTI_WORD_REVERSE_SPANS.push({ words, abbr });
  }
}
MULTI_WORD_REVERSE_SPANS.sort((a, b) => b.words.length - a.words.length);

// Single-word reverse token map: "operations" -> "ops"
const SINGLE_TOKEN_REVERSE: Record<string, string> = {};
for (const [abbr, expansion] of Object.entries(TOKEN_ABBREVIATIONS)) {
  if (!expansion.includes(' ')) {
    SINGLE_TOKEN_REVERSE[expansion] = abbr;
  }
}

/** Cartesian product of arrays. [[a,b],[c],[d,e]] -> [[a,c,d],[a,c,e],[b,c,d],[b,c,e]] */
function cartesianProduct(arrays: string[][]): string[][] {
  if (arrays.length === 0) return [[]];
  return arrays.reduce<string[][]>(
    (acc, curr) => acc.flatMap((combo) => curr.map((item) => [...combo, item])),
    [[]]
  );
}

const MAX_EXPANDABLE_TOKENS = 4;

// A vice president's function reads with and without "of": "VP Sales" is
// also "VP of Sales" and "Vice President of Sales" (substring matches missed
// both). A rank followed by a word gets the same spellings again with "of"
// between them. The API's people_utils.py makes the same list.
const VP_RANKS = new Set([
  'vp',
  'svp',
  'evp',
  'avp',
  'vice president',
  'senior vice president',
  'executive vice president',
  'assistant vice president',
]);

/** `alternatives` with "of" after the first vice-president rank written
 * plainly ("vp", not "vp,") that a word follows, or null. */
function withOf(alternatives: string[][]): string[][] | null {
  for (let i = 0; i < alternatives.length - 1; i++) {
    const here = alternatives[i];
    const after = alternatives[i + 1];
    const cleanAfter = after[0].replace(/[^a-z0-9]/g, '');
    if (
      here.some((form) => VP_RANKS.has(form)) &&
      /^[a-z0-9 ]+$/.test(here[0]) &&
      cleanAfter !== '' &&
      !STOP_WORDS.has(cleanAfter) &&
      !STOP_WORDS.has(after[0])
    ) {
      return [
        ...alternatives.slice(0, i + 1),
        ['of'],
        ...alternatives.slice(i + 1),
      ];
    }
  }
  return null;
}

/**
 * Abbreviations that are never added as a spelling of their own. The search
 * provider matches a title term as a whole word, so an abbreviation added for a
 * longer title is a second search of its own, and as a title word these fifteen
 * mean something else, or far more, than the role. Live: "de" matched 156,033
 * US profiles ("Gerente de Ventas") beside 64,871 data engineers; "se" was "VP
 * Sales, SE Region"; "fe" Santa Fe; "rec" Parks & Rec. Compared against the
 * whole spelling, lowercase: "ml engineer" and "ops manager" stay, and a user
 * who typed "PM" still gets "product manager". Mirrors the API's title
 * compiler.
 */
export const AMBIGUOUS_TITLE_ABBREVIATIONS: ReadonlySet<string> = new Set([
  'ai',
  'am',
  'be',
  'de',
  'em',
  'fe',
  'fs',
  'ml',
  'mop',
  'ops',
  'pm',
  'qa',
  'rec',
  'se',
  'sse',
]);

/**
 * Get all synonym expansions for a title query. Three strategies:
 *
 * 1. Whole-string forward: "CEO" -> ["chief executive officer"]
 * 2. Whole-string reverse: "chief executive officer" -> ["ceo"]
 * 3. Cross-product token expansion: "VP of RevOps" -> all 4 combos of
 *    expanded/unexpanded per token (VP/Vice President x RevOps/Revenue Operations)
 *
 * A spelling that is exactly one of AMBIGUOUS_TITLE_ABBREVIATIONS is left out
 * ("Data Engineer" adds no "de").
 */
export function getAbbreviationExpansions(title: string): string[] {
  return expandAbbreviations(title).filter(
    (spelling) => !AMBIGUOUS_TITLE_ABBREVIATIONS.has(spelling.toLowerCase())
  );
}

function expandAbbreviations(title: string): string[] {
  const results: string[] = [];
  const normalized = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');
  const normalizedSpaced = title.toLowerCase().trim();

  // Strategy 1: Whole-string forward (abbreviation -> full form)
  const forward = JOB_TITLE_ABBREVIATION_EXPANSIONS[normalized] || [];
  results.push(...forward);

  // Strategy 2: Whole-string reverse (full form -> abbreviation)
  const reverse = REVERSE_TITLE_MAP[normalizedSpaced] || [];
  results.push(...reverse);

  // Strategy 3: Cross-product token expansion for compound titles
  const rawTokens = title.trim().split(/\s+/);
  if (rawTokens.length > 1) {
    // Build alternatives per token position.
    // Each position has [original, expanded] or just [original] for stop words / unknowns.
    const alternatives: string[][] = [];
    let i = 0;
    while (i < rawTokens.length) {
      const tokenLower = rawTokens[i].toLowerCase();
      const clean = tokenLower.replace(/[^a-z0-9]/g, '');

      if (STOP_WORDS.has(clean) || STOP_WORDS.has(tokenLower)) {
        alternatives.push([tokenLower]);
        i++;
        continue;
      }

      // Try multi-word reverse spans first (greedy, longest match).
      // This handles input like "Vice President" -> alternatives: ["vice president", "vp"]
      let multiMatched = false;
      for (const span of MULTI_WORD_REVERSE_SPANS) {
        if (i + span.words.length <= rawTokens.length) {
          const slice = rawTokens
            .slice(i, i + span.words.length)
            .map((t) => t.toLowerCase());
          if (
            slice.length === span.words.length &&
            slice.every((w, j) => w === span.words[j])
          ) {
            alternatives.push([slice.join(' '), span.abbr]);
            i += span.words.length;
            multiMatched = true;
            break;
          }
        }
      }
      if (multiMatched) continue;

      // Try single-token forward expansion: "vp" -> ["vp", "vice president"]
      const expansion = TOKEN_ABBREVIATIONS[clean];
      if (expansion) {
        alternatives.push([tokenLower, expansion]);
        i++;
        continue;
      }

      // Try single-token reverse: "operations" -> ["operations", "ops"]
      const reverseAbbr = SINGLE_TOKEN_REVERSE[clean];
      if (reverseAbbr) {
        alternatives.push([tokenLower, reverseAbbr]);
        i++;
        continue;
      }

      // No expansion available
      alternatives.push([tokenLower]);
      i++;
    }

    // Count expandable positions (those with > 1 alternative)
    const expandableCount = alternatives.filter((a) => a.length > 1).length;

    if (expandableCount > 0) {
      // The spellings as written, then a VP rank's again with "of".
      const ofSpelled = withOf(alternatives);
      for (const spelled of ofSpelled
        ? [alternatives, ofSpelled]
        : [alternatives]) {
        if (expandableCount > MAX_EXPANDABLE_TOKENS) {
          // Fallback: too many combinations. Just produce fully-expanded + fully-abbreviated.
          const fullyExpanded = spelled.map((a) => a[a.length - 1]).join(' ');
          const fullyAbbreviated = spelled.map((a) => a[0]).join(' ');
          if (fullyExpanded !== normalizedSpaced) results.push(fullyExpanded);
          if (
            fullyAbbreviated !== normalizedSpaced &&
            fullyAbbreviated !== fullyExpanded
          ) {
            results.push(fullyAbbreviated);
          }
        } else {
          // Cross-product: generate all 2^N combinations
          const combos = cartesianProduct(spelled);
          for (const combo of combos) {
            const variant = combo.join(' ');
            // Don't include the original (caller adds it)
            if (variant !== normalizedSpaced) {
              results.push(variant);
            }
          }
        }
      }
    }
  }

  return results;
}

export function dedupeCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const value of values) {
    const normalized = value.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    deduped.push(value);
  }
  return deduped;
}

/** Full title selection plus abbreviation variants, shared by the portal and
 * Anywhere. Bound combinations per title in getAbbreviationExpansions, rather
 * than silently dropping selected titles or variants from later personas. */
export function expandJobTitleTerms(titles: string[]): string[] {
  const originals = dedupeCaseInsensitive(titles.map((title) => title.trim()));
  return dedupeCaseInsensitive(
    originals.flatMap((title) => [title, ...getAbbreviationExpansions(title)])
  );
}

// ─── Best-Match Employer Selection ───────────────────────────────────
// When a person has multiple current_employers entries, pick the one that
// best matches the active search filters instead of blindly using [0].

export interface EmployerRecord {
  name?: string;
  employer_name?: string;
  title?: string;
  employee_title?: string;
  company_headcount_latest?: number;
  company_headcount_range?: string;
  company_hq_location?: string;
  company_headquarters_country?: string;
  employee_location?: string;
  employer_is_default?: boolean;
  /** Set by the backend on the employer that matched the search (CLE-429). */
  is_search_match?: boolean;
  [key: string]: unknown;
}

export interface EmployerMatchCriteria {
  titles?: string[];
  headcountRanges?: string[];
  locations?: string[];
}

export function findBestMatchingEmployer(
  employers: EmployerRecord[],
  criteria: EmployerMatchCriteria
): EmployerRecord {
  if (!employers?.length) return {} as EmployerRecord;
  if (employers.length === 1) return employers[0];

  // The backend already knows which employer matched the search (CLE-429);
  // the scoring below only covers rows cached before it tagged them.
  const serverMatch = employers.find((e) => e.is_search_match === true);
  if (serverMatch) return serverMatch;

  const hasAnyCriteria =
    (criteria.titles?.length ?? 0) > 0 ||
    (criteria.headcountRanges?.length ?? 0) > 0 ||
    (criteria.locations?.length ?? 0) > 0;

  if (!hasAnyCriteria) {
    return employers.find((e) => e.employer_is_default) || employers[0];
  }

  let bestMatch = employers[0];
  let bestScore = -1;

  for (const employer of employers) {
    let score = 0;
    const empTitle = (
      employer.employee_title ||
      employer.title ||
      ''
    ).toLowerCase();
    const empHeadcount = employer.company_headcount_range || '';
    const empLocation = (
      employer.company_hq_location ||
      employer.company_headquarters_country ||
      employer.employee_location ||
      ''
    ).toLowerCase();

    // Title match (highest weight) - abbreviation-aware token matching.
    // Expands search criteria using getAbbreviationExpansions so "VP RevOps"
    // matches employer title "Vice President of Revenue Operations".
    if (criteria.titles?.length) {
      let titleMatched = false;
      for (const t of criteria.titles) {
        if (!t || !t.trim()) continue;
        const tLower = t.toLowerCase();
        // Try direct substring first (fast path)
        if (
          empTitle &&
          (empTitle.includes(tLower) || tLower.includes(empTitle))
        ) {
          score += 10;
          titleMatched = true;
          break;
        }
        // Try all abbreviation variants with token-level matching
        const variants = getAbbreviationExpansions(t);
        for (const variant of variants) {
          const variantLower = variant.toLowerCase();
          // Extract meaningful tokens (skip stop words)
          const tokens = variantLower
            .split(/\s+/)
            .map((w) => w.replace(/[^a-z0-9]/g, ''))
            .filter((w) => w && !STOP_WORDS.has(w));
          if (
            tokens.length > 0 &&
            tokens.every((tok) => empTitle.includes(tok))
          ) {
            score += 10;
            titleMatched = true;
            break;
          }
        }
        if (titleMatched) break;
      }
    }

    // Headcount range match
    if (criteria.headcountRanges?.length) {
      const normalizedEmp = empHeadcount.replace(/,/g, '').trim();
      if (
        criteria.headcountRanges.some(
          (r) => r.replace(/,/g, '').trim() === normalizedEmp
        )
      ) {
        score += 5;
      }
    }

    // Location match
    if (criteria.locations?.length) {
      for (const loc of criteria.locations) {
        if (empLocation.includes(loc.toLowerCase())) {
          score += 3;
          break;
        }
      }
    }

    // Tiebreaker: prefer default employer
    if (employer.employer_is_default) score += 1;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = employer;
    }
  }

  return bestMatch;
}

// ─── Crust Data Native Filter Builders ──────────────────────────────
// Uses native (.) fuzzy, [.] substring, and = exact operators instead of
// autocomplete-expand-to-in pipelines. See docs/plans/crustdata-people-search-api.md.

type FilterCondition = Record<string, unknown>;

/**
 * Build a fuzzy (.) or exact (=) filter for one or more text values.
 * Multiple values are wrapped in an OR group.
 */
export function addFuzzyFilter(
  conditions: FilterCondition[],
  column: string,
  values: string[],
  matchType: 'contains' | 'exact' | 'similar' = 'contains'
) {
  if (values.length === 0) return;
  const op =
    matchType === 'exact' ? '=' : matchType === 'similar' ? '(.)' : '[.]';
  if (values.length === 1) {
    conditions.push({ column, type: op, value: values[0] });
  } else {
    // Multi-value OR must use the v1 ``group_filter`` envelope so
    // wrapFilters/addContainsFilter and this helper all speak the
    // same dialect. The older ``{op, conditions}`` shape was silently
    // dropped by the v1 persondb parser — title/skill/location OR
    // groups still fired the request but with the filter stripped.
    conditions.push({
      type: 'group_filter',
      op: 'or',
      filters: values.map((v) => ({ column, type: op, value: v })),
    });
  }
}

/**
 * Build an in/not_in filter for enum values (seniority, industries, headcount).
 * These are case-sensitive exact-match lists against known Crust Data enum values.
 */
export function addEnumFilter(
  conditions: FilterCondition[],
  column: string,
  values: string[],
  exclude = false
) {
  if (values.length === 0) return;
  conditions.push({
    column,
    type: exclude ? 'not_in' : 'in',
    value: values,
  });
}

/**
 * Build a contains/not_contains filter for location exclusions.
 * Exclusion locations still use the autocomplete-expand pipeline on the backend
 * since Crust Data has no native negated fuzzy operator.
 */
export function addContainsFilter(
  conditions: FilterCondition[],
  column: string,
  values: string[],
  filterType: 'contains' | 'not_contains' = 'contains'
) {
  if (values.length === 0) return;
  if (values.length === 1) {
    conditions.push({ type: filterType, column, value: values[0] });
  } else {
    conditions.push({
      type: 'group_filter',
      op: filterType === 'not_contains' ? 'and' : 'or',
      filters: values.map((v) => ({ type: filterType, column, value: v })),
    });
  }
}

/** Short column names for one structured location surface. */
export interface StructuredLocationColumns {
  city: string;
  state: string;
  country: string;
  /** Free-text fallback column for values that cannot be decomposed. */
  blob: string;
}

/** Person location — the person's own address. */
export const PERSON_LOCATION_COLUMNS: StructuredLocationColumns = {
  city: 'location_city',
  state: 'location_state',
  country: 'location_country',
  blob: 'region',
};

/**
 * Compile committed location values into Crustdata's LABELLED location
 * columns instead of a substring test against the free-text blob.
 *
 * Why this beats the old ``region [.] "Manchester, New Hampshire, United
 * States"`` approach:
 *
 *  - Multiple cities become ONE ``in`` list, which is OR by definition — no
 *    hand-built group, and no risk of the values AND-ing into an empty set.
 *  - city / state / country are independent columns, so they AND correctly.
 *    The blob approach could not do this (two substring tests over one
 *    string under-matched), which is why the builders used to DROP the
 *    country filter when a city was picked — the reason a US-scoped search
 *    could return Australian rows.
 *
 * Values that carry no components (a bare "Manchester", or a metro aggregate
 * like "Bay Area") cannot be decomposed. Rather than guess, those stay on the
 * substring path and are OR'd alongside the structured branch, so older saved
 * searches and agent-written filters keep working unchanged.
 */
export function addStructuredLocationFilter(
  conditions: FilterCondition[],
  columns: StructuredLocationColumns,
  values: string[],
  /**
   * Countries selected in the sibling picker, used ONLY to constrain values
   * that carry no country of their own.
   *
   * A decomposed triple already names its country, so the country picker is
   * redundant against it. A BARE value ("Perth", "Bay Area") names nothing —
   * and dropping the country filter next to one is exactly how a US-scoped
   * search returned Australian rows. Live: bare "Perth" with the country
   * dropped is 691,896 rows led by Perth, Western Australia; the same search
   * with the country kept is 10 rows in Perth, North Dakota.
   */
  fallbackCountries: string[] = []
) {
  if (values.length === 0) return;
  const { cities, states, countries, unresolved } =
    decomposeLocationValues(values);

  const structured: FilterCondition[] = [];
  if (cities.length)
    structured.push({ column: columns.city, type: 'in', value: cities });
  if (states.length)
    structured.push({ column: columns.state, type: 'in', value: states });
  if (countries.length)
    structured.push({ column: columns.country, type: 'in', value: countries });

  const branches: FilterCondition[] = [];
  if (structured.length === 1) {
    branches.push(structured[0]);
  } else if (structured.length > 1) {
    branches.push({ type: 'group_filter', op: 'and', filters: structured });
  }
  // The whole picked string on the blob, OR'd with the structured branch
  // rather than replacing it.
  //
  // The picker autocompletes against `region` — free text, whatever each
  // profile says — while the columns above are Crustdata's separately
  // populated normalized set. A suggested value can simply be absent from
  // them: "St Petersburg, Florida, United States" is 1,172 people in
  // `region` and 0 in `location_city`. `region` is also far denser (San
  // Francisco: 1.7M vs 695k). Neither is a superset, so search both.
  for (const value of values) {
    if (splitLocationParts(value).length >= 2) {
      branches.push({ column: columns.blob, type: '[.]', value: value.trim() });
    }
  }
  for (const value of unresolved) {
    const leaf: FilterCondition = {
      column: columns.blob,
      type: '[.]',
      value,
    };
    branches.push(
      fallbackCountries.length
        ? {
            type: 'group_filter',
            op: 'and',
            filters: [
              leaf,
              {
                column: columns.country,
                type: 'in',
                value: fallbackCountries,
              },
            ],
          }
        : leaf
    );
  }

  if (branches.length === 0) return;
  if (branches.length === 1) {
    conditions.push(branches[0]);
    return;
  }
  // Structured picks and undecomposable legacy values are ALTERNATIVES —
  // a row satisfying either should match, so they OR rather than AND.
  conditions.push({ type: 'group_filter', op: 'or', filters: branches });
}

/**
 * Build a range filter (gte/lte) for numeric fields.
 */
export function addRangeFilter(
  conditions: FilterCondition[],
  column: string,
  op: 'gte' | 'lte' | 'gt' | 'lt',
  value: number
) {
  conditions.push({ type: 'range_filter', column, filter_type: op, value });
}

/**
 * Wrap conditions in a group_filter AND structure.
 */
export function wrapFilters(conditions: FilterCondition[]): FilterCondition {
  if (conditions.length === 0) return {};
  return { type: 'group_filter', op: 'and', filters: conditions };
}

// ─── LinkedIn URL normalization ──────────────────────────────────────
//
// Crustdata stores company LinkedIn URLs in PersonDB in one specific
// canonical form: lowercase host, no trailing slash, ``www`` subdomain.
// An equality filter against ``current_employers.company_linkedin_profile_url``
// is byte-exact, so a user-pasted URL with a trailing slash (the form
// LinkedIn's address bar shows when you visit a company page) returns
// **zero results** — live-verified: ``…/openai`` matches 12,220 people,
// ``…/openai/`` matches 0.
//
// The /identify pre-pass already normalizes (it returned the same
// canonical record for ``https://de.linkedin.com/company/openai`` and
// ``  https://www.linkedin.com/company/openai/  ``), but bulk-paste
// flows skip /identify and write user input straight into
// ``currentCompanyLinkedInUrls``. Apply this helper at every write site
// and defensively in buildCrustdataFilters so existing saved searches
// with bad data also start working.

const COMPANY_PATH_RE = /^\/(company|school|showcase)\/[^/?#]+/i;

/**
 * Normalize a company LinkedIn URL to the form Crustdata stores in
 * PersonDB. Returns null for inputs that aren't a recognizable LinkedIn
 * company URL (caller decides whether to keep or drop them).
 *
 * Rules (all live-verified against /api/v1/identify/company):
 * - Trim whitespace.
 * - Lowercase scheme + host.
 * - Collapse regional subdomains (de./fr./ca./…) to ``www.``.
 * - Lowercase the slug (Crustdata stores ``/company/openai`` even when
 *   the user typed ``/company/OpenAI``).
 * - Drop trailing slash, query string, and fragment.
 * - Reject non-``/company/<slug>`` paths (e.g. ``/in/<person>``).
 */
export function normalizeCompanyLinkedInUrl(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Add a scheme so URL parsing handles bare ``linkedin.com/company/x``.
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (!host.endsWith('linkedin.com')) return null;
  const match = parsed.pathname.match(COMPANY_PATH_RE);
  if (!match) return null;
  // Lowercase the slug to match PersonDB storage; preserve the segment
  // type (``company`` / ``school`` / ``showcase``) so filters still
  // distinguish a school URL from a company URL.
  const [, segment] = match[0].split('/');
  const slug = match[0].slice(segment.length + 2).toLowerCase();
  return `https://www.linkedin.com/${segment.toLowerCase()}/${slug}`;
}

/**
 * Apply normalizeCompanyLinkedInUrl to every entry, drop nulls, and
 * dedupe while preserving first-seen order. Used by bulk-paste flows
 * and the wire builder.
 */
export function normalizeCompanyLinkedInUrls(
  urls: readonly string[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    const norm = normalizeCompanyLinkedInUrl(url);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(norm);
  }
  return out;
}

const PERSON_PATH_RE = /^\/in\/[^/?#]+/i;

/**
 * Normalize a PERSON LinkedIn URL to the form Crustdata stores in PersonDB's
 * ``flagship_profile_url`` (the human VANITY url, e.g.
 * ``https://www.linkedin.com/in/janesmith``). Returns null for inputs that
 * aren't a recognizable LinkedIn person URL.
 *
 * Mirrors normalizeCompanyLinkedInUrl but for ``/in/<slug>``: trim, lowercase
 * scheme+host, collapse regional subdomains to ``www``,
 * lowercase the slug, drop trailing slash/query/fragment — so a pasted/stored
 * vanity url byte-matches Crustdata's ``flagship_profile_url`` in an
 * ``in``/``not_in`` filter (the OPAQUE ``linkedin_profile_url`` is the wrong
 * column for what users paste).
 */
export function normalizePersonLinkedInUrl(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (!host.endsWith('linkedin.com')) return null;
  const match = parsed.pathname.match(PERSON_PATH_RE);
  if (!match) return null;
  const [, segment] = match[0].split('/');
  const slug = match[0].slice(segment.length + 2).toLowerCase();
  return `https://www.linkedin.com/${segment.toLowerCase()}/${slug}`;
}

/**
 * Apply normalizePersonLinkedInUrl to every entry, drop nulls, and dedupe
 * while preserving first-seen order. Used by the find-people-by-URL wire
 * builder (and any person-URL bulk-paste flow).
 */
export function normalizePersonLinkedInUrls(urls: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    const norm = normalizePersonLinkedInUrl(url);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(norm);
  }
  return out;
}
